import React, { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Phone, Mail, MapPin, Clock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useLegal, LEGAL_MODALS } from '../context/LegalContext';
import '../styles/contact.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' });
  const { openLegalModal } = useLegal();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    nombre: '',
    telefono: '',
    email: '',
    servicio: '',
    mensaje: '',
    rgpd: false,
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.nombre || !formData.telefono || !formData.mensaje) {
      toast.error('Por favor, completa todos los campos obligatorios');
      return;
    }

    if (!formData.rgpd) {
      toast.error('Debes aceptar la política de privacidad');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact`, formData);

      if (response.data.success) {
        // Open WhatsApp with the pre-filled message to the business number
        // (in addition to the email that the backend already sent).
        const waNumber = '34604150871';
        const waLines = [
          '*Nueva consulta desde la web - Ideas Costa Blanca*',
          '',
          `*Nombre:* ${formData.nombre}`,
          `*Teléfono:* ${formData.telefono}`,
        ];
        if (formData.email) waLines.push(`*Email:* ${formData.email}`);
        if (formData.servicio) waLines.push(`*Servicio:* ${formData.servicio}`);
        waLines.push('', `*Mensaje:*`, formData.mensaje);
        const waUrl = `https://wa.me/${waNumber}?text=${encodeURIComponent(waLines.join('\n'))}`;
        window.open(waUrl, '_blank', 'noopener,noreferrer');

        toast.success(response.data.message || '¡Mensaje enviado con éxito! Nos pondremos en contacto contigo pronto.');
        setFormData({
          nombre: '',
          telefono: '',
          email: '',
          servicio: '',
          mensaje: '',
          rgpd: false,
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      const detail = error?.response?.data?.detail;
      toast.error(detail || 'Error al enviar el formulario. Por favor, inténtalo de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      title: 'Teléfono',
      content: '+34 604 150 871',
      link: 'tel:+34604150871',
    },
    {
      icon: Mail,
      title: 'Email',
      content: 'info@ideascostablanca.com',
      link: 'mailto:info@ideascostablanca.com',
    },
    {
      icon: MapPin,
      title: 'Zona de actuación',
      content: 'Costa Blanca, Alicante',
      link: null,
    },
    {
      icon: Clock,
      title: 'Horario',
      content: 'Lun-Vie: 8:00-19:00 | Sáb: 9:00-14:00',
      link: null,
    },
  ];

  const services = [
    'Reformas Integrales',
    'Construcción en General',
    'Portones Automáticos',
    'Tratamientos de Agua',
    'Climatización y Aerotermia',
    'Mantenimiento Integral',
    'Otro',
  ];

  return (
    <section id="contacto" className="contact" ref={sectionRef}>
      <div className="contact-container">
        <motion.div
          className="section-header centered"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6 }}
        >
          <span className="section-label">CONTACTO</span>
          <h2 className="section-title">Empieza Tu Proyecto Hoy</h2>
        </motion.div>

        <div className="contact-content">
          <motion.div
            className="contact-info"
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }}
            transition={{ duration: 0.8 }}
          >
            <h3 className="contact-info-title">¿Listo para Comenzar?</h3>
            <p className="contact-info-description">
              Contáctanos hoy mismo para una evaluación gratuita. Nuestro equipo responderá a tu solicitud en menos
              de 24 horas.
            </p>

            <div className="contact-items">
              {contactInfo.map((item, index) => {
                const Icon = item.icon;
                return (
                  <motion.div
                    key={index}
                    className="contact-item"
                    initial={{ opacity: 0, x: -20 }}
                    animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
                    transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
                  >
                    <div className="contact-item-icon">
                      <Icon size={20} strokeWidth={1.5} />
                    </div>
                    <div className="contact-item-content">
                      <div className="contact-item-title">{item.title}</div>
                      {item.link ? (
                        <a href={item.link} className="contact-item-text link">
                          {item.content}
                        </a>
                      ) : (
                        <div className="contact-item-text">{item.content}</div>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

          <motion.div
            className="contact-form-wrapper"
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 50 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <form onSubmit={handleSubmit} className="contact-form" noValidate>
              <div className="form-group">
                <label htmlFor="nombre" className="form-label">
                  Nombre completo *
                </label>
                <input
                  type="text"
                  id="nombre"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleChange}
                  className="form-input"
                  data-testid="contact-nombre-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="telefono" className="form-label">
                  Teléfono *
                </label>
                <input
                  type="tel"
                  id="telefono"
                  name="telefono"
                  value={formData.telefono}
                  onChange={handleChange}
                  className="form-input"
                  data-testid="contact-telefono-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="email" className="form-label">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="servicio" className="form-label">
                  Servicio de interés
                </label>
                <select
                  id="servicio"
                  name="servicio"
                  value={formData.servicio}
                  onChange={handleChange}
                  className="form-input form-select"
                >
                  <option value="">Selecciona un servicio</option>
                  {services.map((service, index) => (
                    <option key={index} value={service}>
                      {service}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group full-width">
                <label htmlFor="mensaje" className="form-label">
                  Mensaje *
                </label>
                <textarea
                  id="mensaje"
                  name="mensaje"
                  value={formData.mensaje}
                  onChange={handleChange}
                  className="form-input form-textarea"
                  rows="5"
                  data-testid="contact-mensaje-input"
                ></textarea>
              </div>

              <div className="form-group full-width">
                <label className="form-checkbox-label">
                  <input
                    type="checkbox"
                    name="rgpd"
                    checked={formData.rgpd}
                    onChange={handleChange}
                    className="form-checkbox"
                    data-testid="contact-rgpd-checkbox"
                  />
                  <span>
                    He leído y acepto la{' '}
                    <button
                      type="button"
                      className="privacy-link"
                      onClick={() => openLegalModal(LEGAL_MODALS.PRIVACIDAD)}
                      data-testid="privacy-policy-link"
                    >
                      Política de Privacidad
                    </button>
                    {' *'}
                  </span>
                </label>
              </div>

              <button type="submit" className="btn-submit" disabled={isSubmitting} data-testid="contact-submit-button">
                {isSubmitting ? 'ENVIANDO...' : 'ENVIAR CONSULTA'}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;